// Pure, framework-free helpers for user-scoped draft persistence in
// localStorage. The React surface lives in `hooks/use-persistent-draft.ts`;
// everything here is synchronous, SSR-safe, and unit-testable without React.
//
// Key shape: `titanx:draft:<userId>:<namespace>:<entityId>`. userId lives in
// the KEY (not just the value) so drafts can never leak across logins on a
// shared machine, and so `purgeUserDrafts` can wipe one user's drafts on
// sign-out.
//
// Value shape: a JSON envelope `{ value, savedAt }` — savedAt drives TTL
// expiry on read. Segments are assumed colon-free (userId/entityId are UUIDs,
// namespace is a controlled literal); a stray colon would only over-broaden a
// purge prefix, never cross users.

// Brand-namespaced so draft keys are unmistakable in storage and never collide
// with other app/localStorage keys.
export const DRAFT_PREFIX = 'titanx:draft';

export type DraftEnvelope<T> = {
  value: T;
  savedAt: number; // epoch ms, from Date.now() at write time
};

export type DraftKeyParts = {
  userId: string;
  namespace: string;
  entityId: string;
};

/** Prefix shared by every draft belonging to one user. */
export function buildUserPrefix(userId: string): string {
  return `${DRAFT_PREFIX}:${userId}:`;
}

/** Full localStorage key for a single draft. */
export function buildDraftKey({
  userId,
  namespace,
  entityId,
}: DraftKeyParts): string {
  return `${buildUserPrefix(userId)}${namespace}:${entityId}`;
}

/**
 * True when an envelope is older than `ttlMs`. `Infinity` never expires.
 * Future `savedAt` (clock skew) is treated as fresh.
 */
export function isDraftExpired(
  envelope: Pick<DraftEnvelope<unknown>, 'savedAt'>,
  ttlMs: number,
  now: number
): boolean {
  if (!Number.isFinite(ttlMs)) return false;
  return now - envelope.savedAt > ttlMs;
}

// localStorage is browser-only; every accessor no-ops under SSR.
function hasStorage(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

/**
 * Read a draft envelope, SSR-safe and never-throwing. Returns null when absent,
 * corrupt, malformed, or expired — and self-heals by removing the bad entry so
 * it can't poison future reads.
 */
export function readDraft<T>(
  key: string,
  ttlMs: number,
  now: number = Date.now()
): DraftEnvelope<T> | null {
  if (!hasStorage()) return null;
  const raw = localStorage.getItem(key);
  if (raw === null) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    localStorage.removeItem(key); // corrupt JSON → drop
    return null;
  }

  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    typeof (parsed as Record<string, unknown>).savedAt !== 'number'
  ) {
    localStorage.removeItem(key); // wrong shape → drop
    return null;
  }

  const envelope = parsed as DraftEnvelope<T>;
  if (isDraftExpired(envelope, ttlMs, now)) {
    localStorage.removeItem(key);
    return null;
  }
  return envelope;
}

/**
 * Write a draft envelope, SSR-safe. Best-effort: a quota/serialization failure
 * is swallowed (a lost draft is preferable to a thrown render).
 */
export function writeDraft<T>(
  key: string,
  value: T,
  savedAt: number = Date.now()
): void {
  if (!hasStorage()) return;
  try {
    localStorage.setItem(key, JSON.stringify({ value, savedAt }));
  } catch {
    // quota exceeded / non-serializable — drafting is best-effort
  }
}

/** Remove a single draft by key, SSR-safe. */
export function removeDraft(key: string): void {
  if (!hasStorage()) return;
  localStorage.removeItem(key);
}

/** Remove a single draft by its key parts — the reusable "clear this draft". */
export function clearDraft(parts: DraftKeyParts): void {
  removeDraft(buildDraftKey(parts));
}

/**
 * Remove every draft belonging to `userId`. Call on sign-out so PII in drafts
 * does not outlive the session. Other users' keys are left intact.
 */
export function purgeUserDrafts(userId: string): void {
  if (!hasStorage()) return;
  const prefix = buildUserPrefix(userId);
  // Snapshot keys before removing — deleting in-place shifts indices.
  Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i))
    .filter((key): key is string => key !== null && key.startsWith(prefix))
    .forEach((key) => localStorage.removeItem(key));
}
